import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  TripMap,
  TripMapList,
  useCompareWithVersion,
  useDays,
  useLoading,
  useTripFilters,
  useTripMapState,
  useTripStore,
  useTripTotal,
} from '@koob/trip-designer';
import KoobLoaderFullscreen from '@koob/ui/src/components/KoobLoaderFullscreen';
import { useApp } from '../../../../hooks';
import ComposeMapCompare from './ComposeMapCompare';
import ComposeMapFooter from './ComposeMapFooter';
import ComposeMapTimeline from './ComposeMapTimeline';
import TemplateModal from './TemplateModal';
import { getSuggestedHotels, isTripComplete } from './composeMapTrip';
import useComposeMapBootstrap from './useComposeMapBootstrap';
import useComposeMapMarkers from './useComposeMapMarkers';
import { useTripHotelCardProps } from './tripHotelCard';
import useComposeMapSave from './useComposeMapSave';
import useComposeTimelineRegistry from './useComposeTimelineRegistry';

// The compose screen of the TO: the timeline and its footer on the left, the
// product list or the map on the right. The views draw; what stays here is what
// only the TO does — folders, the guests modal, templates, GTM (in the save
// hook) and the access alerts.
const ComposeMap = ({
  experiences,
  hotels,
  addExperience,
  addHotel,
  tripId,
  hasHydrated,
  getExperienceCompatibility,
  reloadTrip,
  itineraryMode,
  areProgramsLoading,
  trip,
  filterSlot,
  listSlot,
}) => {
  const { t } = useTranslation('tripDesigner');
  const tripFilters = useTripFilters();
  const compareWithVersion = useCompareWithVersion();
  const loading = useLoading();
  const days = useDays();
  const experiencesMap = useTripStore(state => state.experiences);
  const hotelsMap = useTripStore(state => state.hotels);
  const { currentOrganizationPosition } = useApp();
  const hotelCardProps = useTripHotelCardProps();

  const registry = useComposeTimelineRegistry();
  const markers = useComposeMapMarkers({
    addExperience,
    getExperienceCompatibility,
  });
  const mapState = useTripMapState({
    tripId,
    experiences,
    organizationPosition: currentOrganizationPosition,
  });
  const { isNotFirstExperience } = useComposeMapBootstrap({
    tripId,
    hasHydrated,
    areProgramsLoading,
    experiences,
  });
  const save = useComposeMapSave({
    tripId,
    trip,
    tripFilters,
    days,
    experiencesMap,
    hotelsMap,
    reloadTrip,
  });

  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [canSaveAsTemplate, setCanSaveAsTemplate] = useState(true);

  const isLoading = days?.some(item => {
    const hotel = item?.hotelId ? hotelsMap?.[item.hotelId] : null;
    return Boolean(hotel?.isLoading);
  });

  const isComplete = useMemo(
    () => isTripComplete(days, experiencesMap, tripFilters),
    [days, experiencesMap, tripFilters],
  );

  const { hasCurrencyMismatch: currencyMismatch } = useTripTotal();

  const suggestedHotels =
    useMemo(
      () => getSuggestedHotels(days, experiencesMap, hotels),
      [days, experiencesMap, hotels],
    ) ?? [];
  const availableHotels = hotels.filter(
    hotel =>
      !suggestedHotels.some(suggestedHotel => suggestedHotel?.id === hotel?.id),
  );

  return (
    <>
      {!isNotFirstExperience && !loading && (
        <KoobLoaderFullscreen
          opacity="semi"
          title={t('loading.title')}
          description={t('loading.description')}
        />
      )}

      <div
        className={[
          'grid min-h-0 flex-1 grid-rows-[minmax(0,1fr)] gap-4',
          compareWithVersion
            ? 'grid-cols-[minmax(720px,1000px)_1fr]'
            : 'grid-cols-[minmax(400px,480px)_1fr]',
        ].join(' ')}
      >
        <div
          className="relative flex min-h-0 flex-col overflow-hidden rounded-xl border border-solid border-gray-200 bg-white"
          data-testid="td-side-bar"
        >
          <div className="flex shrink-0 items-center border-b border-solid border-gray-200 px-5 py-3">
            <h3 className="text-base font-semibold text-gray-900">
              {t('compose.title')}
            </h3>
          </div>

          <div
            className={['flex min-h-0 flex-1', compareWithVersion && 'gap-5']
              .filter(Boolean)
              .join(' ')}
          >
            {/* Comparing splits the panel into two columns of equal width, each
                scrolling on its own. Both need `min-w-0`, otherwise the
                comparison column claims the whole row and crushes the live one. */}
            <div className="flex min-h-0 min-w-0 flex-1 flex-col">
              <div className="flex-1 overflow-y-auto p-6 space-y-5">
                <ComposeMapTimeline
                  registry={registry}
                  addExperience={addExperience}
                  addHotel={addHotel}
                />
              </div>

              <ComposeMapFooter
                saving={save.saving}
                finalizing={save.finalizing}
                isLoading={isLoading}
                isTripComplete={isComplete}
                canSaveAsTemplate={canSaveAsTemplate}
                hasCurrencyMismatch={currencyMismatch}
                availability={registry}
                onSaveDraft={() => save.handleSave('draft')}
                onSaveTemplate={() =>
                  save
                    .handleSave('template')
                    .then(() => setShowTemplateModal(true))
                }
                onBook={() => save.handleFinalize()}
              />
            </div>

            {compareWithVersion && <ComposeMapCompare tripId={tripId} />}
          </div>
        </div>

        <div className="flex min-h-0 flex-col gap-5">
          {filterSlot}

          <TripMapList translation={t} listSlot={listSlot}>
            <TripMap
              mapState={mapState}
              experiences={experiences}
              getExperienceCompatibility={getExperienceCompatibility}
              hotels={{
                suggested: suggestedHotels,
                available: availableHotels,
              }}
              itineraryMode={itineraryMode}
              markers={markers}
              hotelCardProps={hotelCardProps}
              onAddHotel={addHotel}
            />
          </TripMapList>
        </div>
      </div>

      <TemplateModal
        open={showTemplateModal}
        close={() => setShowTemplateModal(false)}
        onTemplateSaved={() => {
          setCanSaveAsTemplate(false);
        }}
        tripId={tripId}
      />
    </>
  );
};

export default ComposeMap;
